/**
 * D-EZ-107 — A DRIVER'S WORDS, IN HIS OWN VOICE.
 *
 * Given a FACT the dice already settled (he won; X wrecked him; he lost the
 * lead late; the motor let go…), his personality and what he and the other man
 * have been through, this writes a short quote in his voice. A hothead: "He ran
 * into me, plain and simple." An ice-cold man: "We'll look at the tape." A
 * company man: "Great day for the Sunbelt Feed team."
 *
 * ⚠ NARRATIVE ONLY. The choice of line is a hash of (league, race, driver,
 * fact) — never an engine die — so the same race always reads the same way and
 * no race result moves by a single face.
 *
 * ⚠ IT NEVER SAYS WHAT THE FACT DOES NOT. Lines carry no names, numbers or
 * results of their own (`voiceBank.ts`, enforced in `quotes.test.ts`); the only
 * names and numbers that appear come in through the fact's own slots, and a
 * line whose slot the fact cannot fill is never chosen. "Not the first time
 * with him" is used only when the rivalry book shows they DO have history
 * before this race.
 *
 * ⚠ NO WORDS IN A REAL MAN'S MOUTH. A 2025-roster driver (`rw25-…`) gets no
 * written quote: `quote` returns null for him. The printed interview chart's
 * own line, when the dice give him one, is shown as the chart prints it.
 */

import { isRealDriver, stableHash } from '../story/hometown.ts';
import { MILESTONE_WINS } from '../history/milestones.ts';
import type { PeopleContext } from './context.ts';
import { personalityOf } from './context.ts';
import type { Voice } from './personality.ts';
import { rivalryBetween } from './rivalry.ts';
import { MOOD, RIVAL_TAGS, TAGS, VOICE_BANK, type FactKind } from './voiceBank.ts';

/** What happened — only things a ledger already says. */
export type QuoteFactBody =
  | { kind: 'won'; first?: boolean; nth?: number | null }
  | { kind: 'second'; otherId?: string | null }
  | { kind: 'good-run' }
  | { kind: 'bad-day' }
  | { kind: 'wrecked-by'; otherId: string }
  | { kind: 'wrecked'; otherId: string }
  | { kind: 'lost-lead-late'; otherId?: string | null }
  | { kind: 'dnf-mechanical' }
  /** In the playoff, or through to the next round. */
  | { kind: 'clinched' }
  | { kind: 'eliminated' }
  | { kind: 'rival-nearby'; otherId: string }
  | { kind: 'rival-won'; otherId: string }
  | { kind: 'injury-return' }
  | { kind: 'milestone'; what: 'starts' | 'wins'; n: number };

/** Where and when it was said, and the slots the moment can fill. */
export interface QuoteWhere {
  /** A stable id for the race (`SeasonRaceResult`'s raceId, or `season-race` numbers). */
  raceId: string;
  seasonNumber?: number;
  raceNumber?: number;
  /** The name on his hood, when the books know it. */
  sponsor?: string | null;
  /** His shop's name. */
  team?: string | null;
  /**
   * How many times this season he has already had this fact (his wins before
   * this one, say). When given, lines rotate instead of colliding, so a man who
   * wins five times does not say the same thing twice.
   */
  occurrence?: number;
}

export type QuoteFact = QuoteFactBody & QuoteWhere;

export interface Quote {
  driverId: string;
  voice: Voice;
  /** The bank cell used — 'won-first', 'wrecked-by'… */
  kind: FactKind;
  text: string;
  /** True when a "we have history" line was added (the book showed prior incidents). */
  history: boolean;
}

function bankKind(f: QuoteFactBody): FactKind {
  switch (f.kind) {
    case 'won':
      if (f.first) return 'won-first';
      if (f.nth && MILESTONE_WINS.includes(f.nth)) return 'won-milestone';
      return 'won';
    case 'milestone':
      return f.what === 'wins' ? 'won-milestone' : 'milestone-starts';
    default:
      return f.kind;
  }
}

function ordinal(n: number): string {
  const v = n % 100;
  const s = v >= 11 && v <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${s}`;
}

/** A surname, the way one driver talks about another. */
export function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  const last = parts[parts.length - 1] ?? name;
  return /^(jr\.?|sr\.?|ii|iii|iv)$/i.test(last) && parts.length > 1 ? parts[parts.length - 2] : last;
}

function nameIn(ctx: PeopleContext, id: string): string | null {
  const e = ctx.registry.byId[id];
  if (e) return e.name;
  for (let i = ctx.seasons.length - 1; i >= 0; i--) {
    const c = ctx.seasons[i].roster.find((x) => x.driverId === id);
    if (c) return c.name;
  }
  return null;
}

/** The slot values a fact can fill. A missing one rules a line out. */
export function slotsFor(fact: QuoteFact, ctx: PeopleContext): Record<string, string> {
  const slots: Record<string, string> = {};
  const otherId = 'otherId' in fact ? fact.otherId : null;
  if (otherId) {
    const n = nameIn(ctx, otherId);
    if (n) slots.other = surname(n);
  }
  if (fact.sponsor) slots.sponsor = fact.sponsor;
  if (fact.team) slots.team = fact.team;
  const n = fact.kind === 'milestone' ? fact.n : fact.kind === 'won' ? (fact.nth ?? null) : null;
  if (n !== null && n !== undefined && n > 0) {
    slots.n = String(n);
    slots.nth = ordinal(n);
  }
  return slots;
}

const SLOT = /\{(\w+)\}/g;

export function fillable(line: string, slots: Record<string, string>): boolean {
  for (const m of line.matchAll(SLOT)) if (!(m[1] in slots)) return false;
  return true;
}

export function fill(line: string, slots: Record<string, string>): string {
  const out = line.replace(SLOT, (_, k: string) => slots[k] ?? '');
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/** Did the two men have history BEFORE this moment? */
function hadHistory(ctx: PeopleContext, a: string, b: string, at: QuoteWhere): boolean {
  const rec = rivalryBetween(ctx.book, a, b);
  if (!rec) return false;
  if (at.seasonNumber === undefined || at.raceNumber === undefined) return rec.moments.length > 1;
  return rec.moments.some(
    (m) => m.seasonNumber < (at.seasonNumber as number) || (m.seasonNumber === at.seasonNumber && m.raceNumber < (at.raceNumber as number)),
  );
}

/**
 * His words for a fact, in his voice — or null for a real driver, or when no
 * line can be written from what the fact holds.
 */
export function quote(fact: QuoteFact, driverId: string, ctx: PeopleContext): Quote | null {
  if (isRealDriver(driverId)) return null;
  const persona = personalityOf(driverId, ctx);
  let kind = bankKind(fact);
  const slots = slotsFor(fact, ctx);
  if (kind === 'won-milestone' && !slots.n) kind = 'won';
  const voice = persona.voice;
  let lines = VOICE_BANK[voice][kind].filter((l) => fillable(l, slots));
  if (lines.length === 0) lines = VOICE_BANK.plain[kind].filter((l) => fillable(l, slots));
  if (lines.length === 0) return null;

  const pickKey = `quote|${ctx.leagueKey}|${driverId}|${kind}`;
  const idx =
    fact.occurrence !== undefined
      ? (stableHash(`${pickKey}|${fact.seasonNumber ?? ''}`) + fact.occurrence) % lines.length
      : stableHash(`${pickKey}|${fact.raceId}`) % lines.length;
  let text = fill(lines[idx], slots);

  const otherId = 'otherId' in fact ? fact.otherId : null;
  const history = Boolean(otherId && slots.other && hadHistory(ctx, driverId, otherId, fact));
  const h = stableHash(`tag|${ctx.leagueKey}|${fact.raceId}|${driverId}|${kind}`);
  let usedHistory = false;
  if (history && h % 3 !== 0) {
    const rt = RIVAL_TAGS[voice];
    text += ` ${rt[(h >>> 3) % rt.length]}`;
    usedHistory = true;
  } else {
    const tags = TAGS[voice][MOOD[kind]];
    const r = h % (tags.length + 2);
    if (r < tags.length) text += ` ${tags[r]}`;
  }
  return { driverId, voice, kind, text, history: usedHistory };
}
